import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
  output,
  signal,
} from '@angular/core';
import { Router } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { NgOptimizedImage } from '@angular/common';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { DestroyRef } from '@angular/core';
import { ModalComponent, ModalConfig } from '../modal/modal';
import { ModalStatusComponent } from '../modal-status/modal-status';
import { HuxleyComponent } from '../huxley/huxley';
import { SidebarPlanWidgetComponent } from '../sidebar-plan-widget/sidebar-plan-widget';
import { StateStoreService } from '../../services/state-store/state-store';
import { ThemeService } from '../../services/theme/theme.service';
import { appLogoUrlForTheme } from '../../constants/app-branding.constants';
import { PlanId, formatCurrency } from '../../constants/billing.constants';

@Component({
  selector: 'app-request-review-modal',
  imports: [
    FormsModule,
    NgOptimizedImage,
    TranslocoModule,
    ModalComponent,
    ModalStatusComponent,
    HuxleyComponent,
    SidebarPlanWidgetComponent,
  ],
  templateUrl: './request-review-modal.html',
  styleUrl: './request-review-modal.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RequestReviewModalComponent {
  private readonly stateService = inject(StateStoreService);
  private readonly themeService = inject(ThemeService);
  private readonly router = inject(Router);
  private readonly transloco = inject(TranslocoService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly translationVersion = signal(0);

  readonly isVisible = input(false);
  readonly isRequesting = input(false);
  readonly errorMessage = input<string | null>(null);
  /** True when `errorMessage` is an access denial (403) rather than a
   *  failure — renders the amber `.denied-banner` instead of the red
   *  error. Parent resolves it with `denialOrFallback`. */
  readonly errorIsDenial = input(false);
  readonly successMessage = input<string | null>(null);
  readonly assessmentId = input<string | null>(null);
  readonly assessmentName = input<string | null>(null);

  readonly confirmed = output<{ assessmentId: string; message?: string }>();
  readonly cancelled = output<void>();
  readonly closed = output<void>();

  // Public so tests can manipulate / assert.
  readonly message = signal('');
  readonly internalError = signal<string | null>(null);

  // Combine local validation errors with parent-supplied async errors.
  readonly error = computed(() => this.internalError() ?? this.errorMessage());
  // Local validation errors are always failures, never denials.
  readonly bannerIsDenial = computed(() => !this.internalError() && this.errorIsDenial());

  readonly logoUrl = computed(() => appLogoUrlForTheme(this.themeService.theme()));

  protected readonly managedBy = this.stateService.managedBy;
  protected readonly organizations = this.stateService.organizations;
  protected readonly hasActiveSubscription = this.stateService.hasActiveSubscription;

  /** Price per review derived from the active subscription, falling
   *  back to Trust Center (cheapest current plan) when there's no
   *  subscription yet. Post-VER-143 AI_RISK_ASSESSMENT is retired, so
   *  the fallback moved to the next-cheapest self-checkout plan. */
  protected readonly pricePerReview = computed(() => {
    const sub = this.stateService.subscription();
    const plans = this.stateService.productPlans();
    const defaultPlan = plans[PlanId.TRUST_CENTER_PACKAGE];

    if (!sub) return defaultPlan?.pricePerReview ?? null;
    if (sub.plan_id === PlanId.CUSTOM) return sub.custom_price_per_review ?? null;
    return plans[sub.plan_id]?.pricePerReview ?? defaultPlan?.pricePerReview ?? null;
  });

  protected readonly formattedPrice = computed(() => {
    this.translationVersion();
    const price = this.pricePerReview();
    return price === null
      ? this.transloco.translate('modals.request_review.contact_for_pricing')
      : formatCurrency(price, 'DKK');
  });

  protected readonly modalConfig = computed<ModalConfig>(() => {
    this.translationVersion();
    return {
      title: this.transloco.translate('modals.request_review.modal_title'),
      size: 'medium',
      showLogo: true,
      showCloseButton: !this.successMessage(),
      showFooter: !this.successMessage(),
      primaryButtonText: this.transloco.translate('modals.request_review.modal_primary'),
      secondaryButtonText: this.transloco.translate('common.cancel'),
    };
  });

  constructor() {
    this.transloco.langChanges$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.translationVersion.update(v => v + 1));
  }

  protected readonly primaryButtonDisabled = computed(
    () => this.isRequesting() || !this.hasActiveSubscription()
  );

  onModalConfirmed(): void {
    const id = this.assessmentId();
    if (!id) {
      this.internalError.set(
        this.transloco.translate('modals.request_review.assessment_id_missing')
      );
      return;
    }
    this.internalError.set(null);
    this.confirmed.emit({ assessmentId: id, message: this.message() || undefined });
  }

  onModalCancelled(): void {
    this.resetForm();
    this.cancelled.emit();
  }

  onModalClosed(): void {
    this.resetForm();
    this.closed.emit();
  }

  protected onMessageInput(event: Event): void {
    this.message.set((event.target as HTMLTextAreaElement).value);
  }

  protected clearError(): void {
    this.internalError.set(null);
  }

  protected navigateToPricing(): void {
    this.router.navigate(['/billing'], { queryParams: { tab: 'subscription' } });
  }

  private resetForm(): void {
    this.message.set('');
    this.internalError.set(null);
  }
}
